import React, { useEffect, useRef, useState } from 'react';
import { MessageBoardProps, TTSType, Judgment } from '../types';
import AudioPlayer from './AudioPlayer';
import PirateText from './PirateText';
import InputArea from './InputArea';
import { playBubbleSound, playMessageSound, playSelectSound } from '../utils/sound';

// MessageBoard Component
const MessageBoard = ({
    round,
    playerInitiated,
    playerInsult,
    aiInsult,
    playerResponse,
    aiResponse,
    judgment,
    gamePhase,
    isPlayerTurn,
    isLoading,
    playerInput,
    handleInputChange,
    handleSubmit,
    handlePlayerInsult,
    showNextRoundButton,
    handleNextRound,
    insertTextAtCursor,
    judgmentDisplayed,
    setJudgmentDisplayed
}: MessageBoardProps) => {
    const prevAiInsultRef = useRef<string>('');
    const prevPlayerInsultRef = useRef<string>('');
    const prevPlayerResponseRef = useRef<string>('');
    const prevAiResponseRef = useRef<string>('');
    const prevJudgmentRef = useRef<string>('');
    const [currentIndex, setCurrentIndex] = useState(0);

    // Define message types
    type MessageType = 'player' | 'ai' | 'judgment';

    interface AudioInfo {
        text: string;
        type: TTSType;
        messageId: string;
    }

    interface BaseMessage {
        type: MessageType;
        label?: string;
        audio?: AudioInfo;
    }

    interface PlayerMessage extends BaseMessage {
        type: 'player';
        content: string;
        label: string;
    }

    interface AIMessage extends BaseMessage {
        type: 'ai';
        content: string;
        label: string;
        audio: AudioInfo;
    }

    interface JudgmentMessage extends BaseMessage {
        type: 'judgment';
        content: Judgment;
        audio: AudioInfo;
    }

    type Message = PlayerMessage | AIMessage | JudgmentMessage;

    // Generate message sequence based on game state
    const messageSequence = (): Message[] => {
        const messages: Message[] = [];

        if (playerInitiated && playerInsult) {
            messages.push({
                type: 'player',
                content: playerInsult,
                label: 'Player insult:'
            });
        }

        if (aiInsult) {
            messages.push({
                type: 'ai',
                content: aiInsult,
                label: 'AI insult:',
                audio: { text: aiInsult, type: 'insult', messageId: `ai-insult-${round}` }
            });
        }

        if (!playerInitiated && playerResponse) {
            messages.push({
                type: 'player',
                content: playerResponse,
                label: 'Player comeback:'
            });
        }

        if (playerInitiated && aiResponse) {
            messages.push({
                type: 'ai',
                content: aiResponse,
                label: 'AI comeback:',
                audio: { text: aiResponse, type: 'comeback', messageId: `ai-comeback-${round}` }
            });
        }

        if (judgment.winner) {
            messages.push({
                type: 'judgment',
                content: judgment,
                audio: { text: judgment.explanation, type: 'judgment', messageId: `judgment-${round}` }
            });
        }

        return messages;
    };

    // Auto-advance logic - but only for player messages, not for judgment results
    useEffect(() => {
        // When a new message is added, show the latest message
        const messages = messageSequence();
        if (messages.length > 0) {
            // Don't auto-advance to judgment message
            if (judgment.winner && currentIndex < messages.length - 1) {
                // If judgment exists but we're not showing it yet, show the See Results button
                // The button will be shown by checkShowResultsButton() below
            } else if (!judgment.winner) {
                // Auto-advance to other message types (player/AI messages)
                setCurrentIndex(messages.length - 1);
            }
        } else {
            setCurrentIndex(0);
        }
    }, [playerInsult, aiInsult, playerResponse, aiResponse]);

    // Calculate if we should show the results button
    const showResultsButton = judgment.winner && !judgmentDisplayed;

    // When judgment arrives, don't auto-show it
    useEffect(() => {
        if (judgment.winner) {
            // Reset judgmentDisplayed when new judgment arrives
            setJudgmentDisplayed(false);
        }
    }, [judgment.winner, setJudgmentDisplayed]);

    // Play sound when AI insult appears
    useEffect(() => {
        if (aiInsult && aiInsult !== prevAiInsultRef.current) {
            playBubbleSound();
            prevAiInsultRef.current = aiInsult;
        }
    }, [aiInsult]);

    // Play sound when player insult appears
    useEffect(() => {
        if (playerInsult && playerInsult !== prevPlayerInsultRef.current) {
            playBubbleSound();
            prevPlayerInsultRef.current = playerInsult;
        }
    }, [playerInsult]);

    // Play sound when player response appears
    useEffect(() => {
        if (playerResponse && playerResponse !== prevPlayerResponseRef.current) {
            playBubbleSound();
            prevPlayerResponseRef.current = playerResponse;
        }
    }, [playerResponse]);

    // Play sound when AI response appears
    useEffect(() => {
        if (aiResponse && aiResponse !== prevAiResponseRef.current) {
            playBubbleSound();
            prevAiResponseRef.current = aiResponse;
        }
    }, [aiResponse]);

    // Play sound when judgment appears
    useEffect(() => {
        if (judgment.winner && judgment.winner !== prevJudgmentRef.current) {
            playMessageSound();
            prevJudgmentRef.current = judgment.winner;
        }
    }, [judgment.winner]);

    // Handle player submission - auto-advance to next message 
    useEffect(() => {
        // When player submits response or insult, auto-advance
        if (playerResponse || playerInsult) {
            const messages = messageSequence();
            if (messages.length > 0) {
                setCurrentIndex(messages.length - 1);
            }
        }
    }, [playerResponse, playerInsult]);

    // Get next message manually
    const showNextMessage = () => {
        const messages = messageSequence();
        if (currentIndex < messages.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    // Remove the event handler since we're using a CSS-only solution

    return (
        <div className="message-board nes-container is-dark is-centered">
            <div className="round-display nes-text is-warning">Round {round}</div>

            {/* Get messages once at the beginning */}
            {(() => {
                const messages = messageSequence();

                return (
                    <>
                        {/* Special case for player starting with insult - no messages yet */}
                        {messages.length === 0 && gamePhase === 'playerInsult' && isPlayerTurn && (
                            <>
                                <div className={`message-source nes-text is-primary`}>
                                    PLAYER
                                </div>
                                <div className="current-message">
                                    <div className="message player-message">
                                        <div className="message-label nes-text is-primary">Your Turn</div>
                                        <div className="message-content">Start the round with your best pirate insult!</div>
                                    </div>
                                </div>

                                <div className="player-input-container">
                                    <InputArea
                                        gamePhase={gamePhase}
                                        isPlayerTurn={isPlayerTurn}
                                        isLoading={isLoading}
                                        playerInput={playerInput}
                                        handleInputChange={handleInputChange}
                                        handleSubmit={handleSubmit}
                                        handlePlayerInsult={handlePlayerInsult}
                                        insertTextAtCursor={insertTextAtCursor}
                                    />
                                </div>
                            </>
                        )}

                        {/* Regular message flow when messages exist */}
                        {messages.length > 0 && (() => {
                            if (messages.length === 0) return null;

                            // Ensure currentIndex is valid
                            const safeIndex = Math.min(Math.max(0, currentIndex), messages.length - 1);
                            if (currentIndex !== safeIndex) {
                                setCurrentIndex(safeIndex);
                            }

                            const currentMessage = messages[safeIndex];

                            // Show message source
                            let sourceText = '';
                            let sourceClass = '';
                            if (currentMessage.type === 'player') {
                                sourceText = 'PLAYER';
                                sourceClass = 'is-primary';
                            } else if (currentMessage.type === 'ai') {
                                sourceText = 'ENEMY';
                                sourceClass = 'is-error';
                            } else if (currentMessage.type === 'judgment') {
                                sourceText = 'RESULT';
                                sourceClass = 'is-warning';
                            }

                            return (
                                <>
                                    <div className={`message-source nes-text ${sourceClass}`}>
                                        {sourceText}
                                    </div>

                                    <div className="current-message">
                                        {currentMessage.type === 'player' && (
                                            <div className="message player-message">
                                                <div className="message-label nes-text is-primary">{currentMessage.label}</div>
                                                <div className="message-content">{currentMessage.content}</div>
                                            </div>
                                        )}

                                        {currentMessage.type === 'ai' && (
                                            <div className="message ai-message">
                                                <div className="message-label nes-text is-error">{currentMessage.label}</div>
                                                <div className="message-content">
                                                    <PirateText text={currentMessage.content} />
                                                </div>
                                                <AudioPlayer
                                                    text={currentMessage.audio.text}
                                                    type={currentMessage.audio.type}
                                                    messageId={currentMessage.audio.messageId}
                                                    autoPlay={true}
                                                />

                                                {/* Add response button for AI insult */}
                                                {/* {gamePhase === 'playerResponse' && isPlayerTurn && currentIndex === messages.length - 1 && (
                                                    <div className="message-action">
                                                        <button
                                                            className="respond-button nes-btn is-warning"
                                                            onClick={() => {
                                                                // Focus the input field
                                                                document.getElementById('player-input-field-response')?.focus();
                                                            }}
                                                        >
                                                            Respond
                                                        </button>
                                                    </div>
                                                )} */}
                                            </div>
                                        )}

                                        {currentMessage.type === 'judgment' && (
                                            <div className="message judgment-message">
                                                <div className="message-label nes-text is-warning">Judgment</div>
                                                <div className="message-content">
                                                    <p className="nes-text is-primary">
                                                        <strong>
                                                            Winner: {playerInitiated
                                                                ? (currentMessage.content.winner === 'insult' ? 'Player' : 'AI')
                                                                : (currentMessage.content.winner === 'insult' ? 'AI' : 'Player')
                                                            }
                                                        </strong> ({currentMessage.content.winner})
                                                    </p>
                                                    <p><PirateText text={currentMessage.content.explanation} /></p>

                                                    {/* Integrated Game Over message */}
                                                    {gamePhase === 'gameOver' && (
                                                        <div className="game-over">
                                                            <p className="nes-text is-warning">Game Over!</p>
                                                            <p className="nes-text is-primary">
                                                                {playerInitiated
                                                                    ? (currentMessage.content.winner === 'comeback' ? "AI wins the battle!" : "Player wins the battle!")
                                                                    : (currentMessage.content.winner === 'insult' ? "AI wins the battle!" : "Player wins the battle!")
                                                                }
                                                            </p>
                                                            <button
                                                                className="nes-btn is-primary"
                                                                onClick={() => {
                                                                    // Reset game and go back to title
                                                                    window.location.reload();
                                                                }}
                                                            >
                                                                Back to Title
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                {/* <AudioPlayer
                                                    text={currentMessage.audio.text}
                                                    type={currentMessage.audio.type}
                                                    messageId={currentMessage.audio.messageId}
                                                    autoPlay={false}
                                                /> */}

                                                {/* Add next round button for judgment */}
                                                {showNextRoundButton && currentIndex === messages.length - 1 && (
                                                    <div className="message-action">
                                                        <button
                                                            onClick={handleNextRound}
                                                            className="next-round-btn nes-btn is-primary"
                                                        >
                                                            Next Round
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Input area when player needs to respond */}
                                    {isPlayerTurn && !isLoading && messages.length > 0 && currentIndex === messages.length - 1 && (
                                        <div className="player-input-container">
                                            <InputArea
                                                gamePhase={gamePhase}
                                                isPlayerTurn={isPlayerTurn}
                                                isLoading={isLoading}
                                                playerInput={playerInput}
                                                handleInputChange={handleInputChange}
                                                handleSubmit={handleSubmit}
                                                handlePlayerInsult={handlePlayerInsult}
                                                insertTextAtCursor={insertTextAtCursor}
                                            />
                                        </div>
                                    )}

                                    {isLoading && currentIndex === messages.length - 1 && (
                                        <div className="ai-thinking nes-text is-disabled">
                                            <i className="nes-icon is-small star"></i> Consulting the pirate AI...
                                        </div>
                                    )}

                                    {/* Show Results button when judgment is ready but not yet displayed */}
                                    {showResultsButton && judgment.winner && currentIndex < messages.length - 1 && (
                                        <div className="message-action">
                                            <button
                                                className="see-results-btn nes-btn is-warning"
                                                onClick={() => {
                                                    // Show the judgment message and hide the button
                                                    setCurrentIndex(messages.length - 1);
                                                    setJudgmentDisplayed(true);
                                                }}
                                            >
                                                See Results
                                            </button>
                                        </div>
                                    )}

                                    {/* Next button for navigation between earlier messages */}
                                    {currentIndex < messages.length - 1 && !showResultsButton && (
                                        <button
                                            className="next-message-btn nes-btn is-primary"
                                            onClick={showNextMessage}
                                        >
                                            <span className="arrow-icon">›</span>
                                        </button>
                                    )}
                                </>
                            )
                        })()}
                    </>
                );
            })()}
        </div>
    );
};

export default MessageBoard;
